import Form from 'antd/lib/form';
import * as React from 'react';
import { AppSettingsUpdatable } from '@/actions/updatable';
import { globalMessage } from '@/utils/globalMessage';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface UseFixSettingsModalArgs {
    open: boolean;
    onCancel: () => void;
}

// Shared form + save lifecycle for the fix-request settings modals, which all save
// through the same updatable key.
export const useFixSettingsModal = ({ open, onCancel }: UseFixSettingsModalArgs) => {
    const [form] = Form.useForm();

    const isLoading = useIsLoading(AppSettingsUpdatable.RequiresFixRequestsApprovals);
    const success = useUpdated(AppSettingsUpdatable.RequiresFixRequestsApprovals);
    const error = useError(AppSettingsUpdatable.RequiresFixRequestsApprovals);

    // The updatable key is shared by all 4 (mounted) fix-settings modals, so `open` gates which
    // one reacts. Read open/onCancel via refs so the effects fire only on the save transition,
    // not when open/onCancel change (which would re-toast/re-close on reopen).
    const openRef = React.useRef(open);
    openRef.current = open;
    const onCancelRef = React.useRef(onCancel);
    onCancelRef.current = onCancel;

    React.useEffect(() => {
        if (success && openRef.current) {
            globalMessage.success('Fixes settings updated successfully!');
            onCancelRef.current();
        }
    }, [success]);

    React.useEffect(() => {
        if (error && openRef.current) {
            globalMessage.error(error);
        }
    }, [error]);

    // drop unsaved edits so reopening shows persisted values.
    const afterClose = () => form.resetFields();

    return { form, isLoading, afterClose };
};
